import { useLayoutEffect, type FC } from 'react';
import { Navigate, Route, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { useIipService } from 'src/apis/iip-service/use-iip-service';
import { handleUmmRelatedError } from 'src/apis/iip-service/utils';
import { ThrowNotFound } from 'src/components/feedback/not-found';
import { RootRoutes } from 'src/components/root-routes';
import { PageSpinner } from 'src/components/spinner-container';
import { CreateMaintenanceMessage } from 'src/pages/market-messages/maintenance-messages/create';
import { EditMaintenanceMessage } from 'src/pages/market-messages/maintenance-messages/edit';
import { MaintenanceMessagesOverview } from 'src/pages/market-messages/maintenance-messages/overview';
import { UmmFallback } from 'src/pages/market-messages/umm-fallback';
import { UrgentMarketMessagesCreate } from 'src/pages/market-messages/urgent-market-messages/create';
import { UrgentMarketMessageDetails } from 'src/pages/market-messages/urgent-market-messages/details';
import { UrgentMarketMessageEdit } from 'src/pages/market-messages/urgent-market-messages/edit';
import { UrgentMarketMessagesOverview } from 'src/pages/market-messages/urgent-market-messages/overview';
import { mapOldUmmSearchParams } from 'src/pages/market-messages/utils';

export const MarketMessages = () => {
  const iipService = useIipService({ onError: handleUmmRelatedError });

  if (iipService.error) return <UmmFallback />;
  if (!iipService.response) return <PageSpinner />;

  return (
    <RootRoutes>
      <Route
        path="/market-messages"
        element={<Navigate to="/market-messages/urgent-market-messages" />}
      />
      <Route
        path="/market-messages/urgent-market-messages"
        element={<UrgentMarketMessagesOverview iipService={iipService} />}
      />
      <Route
        path="/market-messages/urgent-market-messages/create"
        element={<UrgentMarketMessagesCreate iipService={iipService} />}
      />
      <Route
        path="/market-messages/urgent-market-messages/:ummId"
        element={<UrgentMarketMessageDetails iipService={iipService} />}
      />
      <Route
        path="/market-messages/urgent-market-messages/:ummId/edit"
        element={<UrgentMarketMessageEdit iipService={iipService} />}
      />
      <Route
        path="/market-messages/maintenance-messages"
        element={<MaintenanceMessagesOverview iipService={iipService} />}
      />
      <Route
        path="/market-messages/maintenance-messages/create"
        element={<CreateMaintenanceMessage iipService={iipService} />}
      />
      <Route
        path="/market-messages/maintenance-messages/:maintenanceMessageId/edit"
        element={<EditMaintenanceMessage iipService={iipService} />}
      />
      {/* Support for legacy URLs */}
      <Route
        path="/umm/create"
        element={
          <Navigate
            to="/market-messages/urgent-market-messages/create"
            replace
          />
        }
      />
      <Route
        path="/umm/:id"
        element={<RedirectLegacyUMMDetailsToUMMDetails />}
      />
      <Route
        path="/umm/:id/edit"
        element={<RedirectLegacyUMMEditToUMMEdit />}
      />

      <Route path="/umm" element={<RedirectLegacyUMMToUMM />} />
      <Route path="*" element={<ThrowNotFound />} />
    </RootRoutes>
  );
};

const RedirectLegacyUMMDetailsToUMMDetails: FC = () => {
  const { id } = useParams<{
    id: string;
  }>();
  return (
    <Navigate replace to={`/market-messages/urgent-market-messages/${id}`} />
  );
};

const RedirectLegacyUMMEditToUMMEdit: FC = () => {
  const { id } = useParams<{
    id: string;
  }>();
  return (
    <Navigate
      replace
      to={`/market-messages/urgent-market-messages/${id}/edit`}
    />
  );
};

const RedirectLegacyUMMToUMM: FC = () => {
  const [searchParams] = useSearchParams();
  const newSearch = mapOldUmmSearchParams(searchParams);

  useLayoutEffect(() => {
    if (searchParams.size !== newSearch.size) {
      toast.warning(
        'You might have used an invalid query parameters that are not supported.'
      );
    }
  }, [searchParams, newSearch]);

  return (
    <Navigate
      to={`/market-messages/urgent-market-messages${newSearch.toString() ? `?${newSearch.toString()}` : ''}`}
      replace
    />
  );
};
